import React, { useState } from "react";
import { Platform, StyleSheet, TextInput } from "react-native";
import Animated from "react-native-reanimated";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";
import { ThemedInputProps } from "./props";
import { withDefaultProps } from "./helpers";
import { handleBlur } from "./validators";
import { handleChangeText } from "./handlers";
import { useInputContextMenu, useInputValidation } from "./hooks";
import useDateTime from "@/hooks/useDateTime";

export default function ThemedInput(props: ThemedInputProps) {
  const p = withDefaultProps(props);
  const { colors } = useTheme();
  const { settings } = useDateTime();
  const [inputFocused, setInputFocused] = useState(false);
  const { isValid, setIsValid } = useInputValidation();
  const { viewRef, inputRef, showContextMenu } = useInputContextMenu(p);

  // Determine background color for items with bgColor
  const itemBgColor = (() => {
    if (!p.items || !p.value) return "transparent";
    const matchedItem = p.items.find((item) => {
      if (typeof item === "string") {
        return item === p.value;
      } else if (typeof item === "object" && item.text) {
        return item.text === p.value;
      }
      return false;
    });
    if (matchedItem && typeof matchedItem === "object" && "bgColor" in matchedItem) {
      return matchedItem.bgColor || "transparent";
    }
    return "transparent";
  })();

  return (
    <Animated.View style={[styles.container, p.style]} ref={viewRef}>
      {p.label && (
        <ThemedText style={[styles.label, !isValid && { color: colors.red.text }, p.labelStyle]} {...p.labelProps}>
          {p.label} {p.required && "*"}
        </ThemedText>
      )}
      <TextInput
        {...p}
        autoFocus={p.autoFocus}
        ref={inputRef}
        placeholderTextColor={colors.placeholder}
        secureTextEntry={p.type === "discrete" ? !inputFocused : p.secureTextEntry}
        multiline={p.multiline}
        style={[
          styles.input,
          { color: colors.text, borderColor: colors.border },
          p.inputStyle,
          inputFocused && { borderColor: colors.primary },
          !isValid && { borderColor: colors.red.border },
          itemBgColor !== "transparent" && { backgroundColor: itemBgColor },
        ]}
        onChangeText={(text) => handleChangeText({ text, ...p })}
        onFocus={() => {
          showContextMenu();
          setInputFocused(true);
        }}
        onBlur={(e) => {
          const value = e.nativeEvent.text;
          const trimmed = value.trim();
          setInputFocused(false);
          if (trimmed !== value) {
            handleChangeText({ text: trimmed, ...p });
          }
          handleBlur({ ...p, value: trimmed, setIsValid, settings });
        }}
      />
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  container: {
    justifyContent: "space-between",
  },
  label: {
    fontSize: 14,
    marginBottom: 4,
    fontWeight: "600",
  },
  input: {
    width: "100%",
    height: 40,
    padding: 8,
    borderRadius: 8,
    borderWidth: StyleSheet.hairlineWidth,
    fontSize: 16,
    ...(Platform.OS === "web" ? { outlineStyle: "none" } : {}),
  },
  button: {
    backgroundColor: "transparent",
    justifyContent: "flex-start",
    borderWidth: 0,
  },
});
